// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: setup calls booked from nudge cards, kept on the device by nudge id, so a booked card
// keeps saying so after switching channels or reloading, and cannot be booked twice.

import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {makeAutoObservable} from 'mobx';

const STORAGE_KEY = 'konvo.nudges.booked';
/** Bookings older than this are forgotten (the card's slots are long gone by then). */
const KEEP_MS = 60 * 24 * 60 * 60 * 1000;

interface Booking {
	/** The slot's start (ISO). */
	start: string;
	/** When it was booked (ms). */
	at: number;
}

function readBookings(): Record<string, Booking> {
	try {
		const raw = getProtectedLocalStorage()?.getItem(STORAGE_KEY);
		const parsed = raw ? (JSON.parse(raw) as unknown) : null;
		if (!parsed || typeof parsed !== 'object') return {};
		const now = Date.now();
		const result: Record<string, Booking> = {};
		for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
			const booking = value as Partial<Booking> | null;
			if (
				booking &&
				typeof booking.start === 'string' &&
				typeof booking.at === 'number' &&
				now - booking.at < KEEP_MS
			) {
				result[id] = {start: booking.start, at: booking.at};
			}
		}
		return result;
	} catch {
		return {};
	}
}

class NudgeBookingsStore {
	booked: Record<string, Booking> = readBookings();
	/** Nudges with a booking on its way (one at a time per card). */
	pending = new Set<string>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	bookedStart(nudgeId: string): string | null {
		return this.booked[nudgeId]?.start ?? null;
	}

	isPending(nudgeId: string): boolean {
		return this.pending.has(nudgeId);
	}

	/** Claims the card for a booking; false when it is booked or a booking is already on its way. */
	begin(nudgeId: string): boolean {
		if (this.booked[nudgeId] || this.pending.has(nudgeId)) return false;
		this.pending.add(nudgeId);
		return true;
	}

	finish(nudgeId: string, start: string | null): void {
		this.pending.delete(nudgeId);
		if (!start) return;
		this.booked = {...this.booked, [nudgeId]: {start, at: Date.now()}};
		try {
			getProtectedLocalStorage()?.setItem(STORAGE_KEY, JSON.stringify(this.booked));
		} catch {
			// Private mode: the booking still shows until the page reloads.
		}
	}
}

export default new NudgeBookingsStore();
